import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    DISPLAY_MODEL_COLOR_OPTIONS,
    supportsDisplayColorSettings,
} from 'src/features/systems/utils/displaySettingConstants';
import { DisplaySettingFormSchemaValues } from 'src/features/systems/utils/displaySettingFormSchema';
import { displaySettingsMessages } from 'src/features/systems/utils/displaySettingsMessages';
import { GATEWAY_DISPLAY_MODEL } from 'src/features/systems/utils/types';
import {
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';

type SettingCode = keyof DisplaySettingFormSchemaValues;

type DisplaySettingCodeInputsProps = {
    form: UseFormReturn<DisplaySettingFormSchemaValues>;
    code: SettingCode;
    sectionTitle: string;
    displayModel: GATEWAY_DISPLAY_MODEL;
};

const ColorSwatch = ({ color }: { color: string }) => (
    <span
        className="border-border inline-block size-4 rounded-sm border"
        style={{ backgroundColor: color }}
        aria-hidden
    />
);

const DisplaySettingCodeInputs = ({ code, sectionTitle, form, displayModel }: DisplaySettingCodeInputsProps) => {
    const { formatMessage } = useIntl();

    const colorOptions = DISPLAY_MODEL_COLOR_OPTIONS[displayModel];
    const showColorFields = supportsDisplayColorSettings(displayModel);

    const renderColorField = (
        fieldName: `${SettingCode}.title_color` | `${SettingCode}.subtitle_color`,
        label: string,
    ) => (
        <FormField
            control={form.control}
            name={fieldName}
            render={({ field }) => (
                <FormItem variant="dialog">
                    <FormLabel variant="dialog">{label}</FormLabel>
                    <FormControlStack>
                        <FormControl>
                            <Select onValueChange={field.onChange} value={(field.value as string | undefined) ?? ''}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder={`${label}...`} />
                                </SelectTrigger>
                                <SelectContent>
                                    {colorOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            <span className="flex items-center gap-2">
                                                <ColorSwatch color={option.value} />
                                                {formatMessage(option.label)}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormControl>
                        <FormMessage />
                    </FormControlStack>
                </FormItem>
            )}
        />
    );

    return (
        <>
            <FormFieldBlockTitle>
                <FormFieldBlockName className="flex flex-row gap-1">{sectionTitle}</FormFieldBlockName>
            </FormFieldBlockTitle>
            <InputFormField
                form={form}
                name={`${code}.title` as const}
                label={formatMessage(displaySettingsMessages.title)}
                required={false}
            />
            <InputFormField
                form={form}
                name={`${code}.subtitle` as const}
                label={formatMessage(displaySettingsMessages.subtitle)}
                required={false}
            />
            {showColorFields && (
                <>
                    {renderColorField(
                        `${code}.title_color` as const,
                        formatMessage(displaySettingsMessages.titleColor),
                    )}
                    {renderColorField(
                        `${code}.subtitle_color` as const,
                        formatMessage(displaySettingsMessages.subtitleColor),
                    )}
                </>
            )}
            <FormFieldBlockSeparator />
        </>
    );
};

export default DisplaySettingCodeInputs;
